import React, { useCallback } from 'react';

import Loadable from 'react-loadable';

import { type RovodalCanvasKeys } from '@atlassian/rovodal-context/canvas-keys';
import { useRovodal } from '@atlassian/rovodal-context/store';

import { ConversationAssistant } from '../../ConversationAssistant';
import { type ConversationAssistantConfig } from '../../types';

import { rovodalCanvasRegistry } from './canvas/rovodalCanvasRegistry';
import { InsideRovodalContext } from './insideRovodalContext';
import { MarkLoadedOnMount } from './MarkLoadedOnMount';
import { rovodalActionsRegistry } from './rovodalActionsRegistry';
import { type RovodalHostState } from './types';

const LoadableRovodal = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_rovodal" */ '@atlassian/rovodal/ui/rovodal'
		).then((m) => m.Rovodal),
	loading: () => null,
});

type RovodalHostEntryProps = {
	rovodalKey: string;
	instanceId?: string;
	config: ConversationAssistantConfig;
};

export const RovodalHostEntry = ({
	rovodalKey,
	instanceId,
	config,
}: RovodalHostEntryProps): React.JSX.Element | null => {
	const [entry, { close, setState }] = useRovodal<RovodalHostState>(rovodalKey, instanceId);

	const markLoaded = useCallback(() => {
		if (!entry?.state?.loaded) {
			setState({ ...(entry?.state as RovodalHostState), loaded: true });
		}
	}, [entry?.state, setState]);

	if (!entry?.isOpen) {
		return null;
	}

	const canvasKey = (entry.state?.canvasKey ?? entry.key) as RovodalCanvasKeys;
	const Canvas = rovodalCanvasRegistry[canvasKey];
	const actions = rovodalActionsRegistry[canvasKey];
	const { uiConfig: uiConfigOverride, ...chatOverridesRest } = entry.state?.chatOverrides ?? {};

	const onClose = () => close();
	const actionsContext = { close: onClose };

	return (
		<LoadableRovodal
			id={rovodalKey}
			isOpen
			fullSizeModal={entry.state?.fullSizeModal}
			onClose={onClose}
			title={entry.state?.title}
			readOnlyAgentHeader={entry.state?.readOnlyAgentHeader}
			showBackButton={entry.state?.showBackButton}
			backLabel={entry.state?.backLabel}
			closeLabel={entry.state?.closeLabel}
			label={entry.state?.label}
			topBarActions={actions?.topBarActions?.(actionsContext)}
			bottomBarActions={actions?.bottomBarActions?.(actionsContext)}
			canvasBarActions={actions?.canvasBarActions?.(actionsContext)}
			canvas={Canvas ? <Canvas rovodalKey={rovodalKey} state={entry.state} /> : null}
			chat={
				<InsideRovodalContext.Provider value={true}>
					<MarkLoadedOnMount onLoaded={markLoaded} />
					<ConversationAssistant
						config={config}
						testId="rovodal-host-conversation-assistant"
						{...chatOverridesRest}
						uiConfig={{
							externalEmbed: true,
							...uiConfigOverride,
							elements: {
								...uiConfigOverride?.elements,
							},
						}}
					/>
				</InsideRovodalContext.Provider>
			}
		/>
	);
};
